import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Container,
    Paper,
    LinearProgress,
    Button,
    Grid,
    Alert
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { fetchSurveyCreatedAt } from '../../services/surveyService';

const CountdownPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [surveyCompletedAt, setSurveyCompletedAt] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [loading, setLoading] = useState(true);

    const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds

    useEffect(() => {
        const fetchSurveyDate = async () => {
            try {
                const response = await fetchSurveyCreatedAt(user._id);
                const completedDate = new Date(response.createdAt);
                setSurveyCompletedAt(completedDate);

                const unlockDate = new Date(completedDate.getTime() + TWO_WEEKS_IN_MS);
                const now = new Date();
                const remaining = unlockDate.getTime() - now.getTime();

                if (remaining <= 0) {
                    setIsUnlocked(true);
                    setTimeRemaining(0);
                } else {
                    setTimeRemaining(remaining);
                }
            } catch (error) {
                console.error('Error fetching survey date:', error);
            } finally {
                setLoading(false);
            }
        };

        if (user && user._id) {
            fetchSurveyDate();
        }
    }, [user, TWO_WEEKS_IN_MS]);

    useEffect(() => {
        if (timeRemaining > 0) {
            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    const newTime = prev - 1000;
                    if (newTime <= 0) {
                        setIsUnlocked(true);
                        clearInterval(timer);
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [timeRemaining]);

    const formatTimeRemaining = (ms) => {
        if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

        const days = Math.floor(ms / (1000 * 60 * 60 * 24));
        const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds };
    };

    const handleViewResults = () => {
        // Navigate to dashboard and let it show the result component
        // The dashboard will need to check if it should show results for control group users
        navigate('/dashboard', { state: { showResults: true } });
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    const { days, hours, minutes, seconds } = formatTimeRemaining(timeRemaining);
    const progress = ((TWO_WEEKS_IN_MS - timeRemaining) / TWO_WEEKS_IN_MS) * 100;

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <Box
                    sx={{
                        background: isUnlocked
                            ? 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)'
                            : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                        color: 'white',
                        p: 3,
                        textAlign: 'center',
                    }}
                >
                    {isUnlocked ? <CheckCircleIcon sx={{ fontSize: 60, mb: 2 }} /> : <AccessTimeIcon sx={{ fontSize: 60, mb: 2 }} />}
                    <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                        {isUnlocked
                            ? t('countdown.unlocked.title', 'Your Results Are Ready!')
                            : t('countdown.locked.title', 'Thank You for Completing the Survey!')
                        }
                    </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    {!isUnlocked ? (
                        <>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography>
                                    {t('countdown.locked.message',
                                        'As a control group participant, your chronotype results will be available 2 weeks after survey completion. This waiting period is an essential part of our research methodology.'
                                    )}
                                </Typography>
                            </Alert>

                            <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', mb: 3 }}>
                                <Typography variant="h6" gutterBottom align="center">
                                    {t('countdown.timeRemaining', 'Time Remaining')}
                                </Typography>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="primary" fontWeight="bold">
                                                {days}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('countdown.days', 'Days')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="primary" fontWeight="bold">
                                                {hours}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('countdown.hours', 'Hours')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="primary" fontWeight="bold">
                                                {minutes}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('countdown.minutes', 'Minutes')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" color="primary" fontWeight="bold">
                                                {seconds}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('countdown.seconds', 'Seconds')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t('countdown.progress', 'Progress')}: {Math.round(progress)}%
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            </Paper>

                            {/* Spacer to push button down */}
                            <Box sx={{ height: '60px' }} />

                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled
                                    startIcon={<LockIcon />}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        bgcolor: 'grey.400',
                                    }}
                                >
                                    {t('countdown.locked.button', 'Results Locked')}
                                </Button>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                                    {surveyCompletedAt && t('countdown.completedOn', 'Survey completed on: {{date}}', {
                                        date: surveyCompletedAt.toLocaleDateString()
                                    })}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Alert severity="success" sx={{ mb: 3 }}>
                                <Typography>
                                    {t('countdown.unlocked.message',
                                        'Congratulations! Your waiting period is complete. You can now view your personalized chronotype analysis and recommendations.'
                                    )}
                                </Typography>
                            </Alert>

                            {/* Add spacing between message and button */}
                            <Box sx={{ height: '40px' }} />

                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleViewResults}
                                    startIcon={<CheckCircleIcon />}
                                    sx={{
                                        px: 6,
                                        py: 1.5,
                                        background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #45a049 0%, #66bb6a 100%)',
                                        }
                                    }}
                                >
                                    {t('countdown.unlocked.button', 'View Your Chronotype Results')}
                                </Button>
                            </Box>

                            {/* Add spacing between button and thank you message */}
                            <Box sx={{ height: '60px' }} />
                        </>
                    )}

                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa' }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            {t('countdown.thankYou',
                                'Thank you for your patience and participation in our research study. Your contribution helps us better understand chronotypes and their impact on daily life.'
                            )}
                        </Typography>
                    </Paper>
                </CardContent>
            </Card>
        </Container>
    );
};

export default CountdownPage;